import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import { Link, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { ORDER_TYPES, UPLOAD_BASE, fmtDateTH, fmtDateTimeTH } from './aoShared'

export default function OrderDetail() {
  const { odId } = useParams()
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // เพิ่มผู้เกี่ยวข้อง
  const [showAdd, setShowAdd] = useState(false)
  const [teachers, setTeachers] = useState([])
  const [selected, setSelected] = useState(new Map())
  const [filter, setFilter] = useState('')
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get(`/api/ao/orders/${odId}`)
      if (res.data.success) setOrder(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [odId])

  useEffect(() => { fetchData() }, [fetchData])

  const alreadyAssigned = useMemo(() => new Set((order?.assignees || []).map(a => a.tb_teacher_t_id)), [order])

  const openAdd = async () => {
    setSelected(new Map())
    setFilter('')
    setModalError('')
    setShowAdd(true)
    if (!teachers.length) {
      try {
        const res = await axiosInstance.get('/api/ao/teachers')
        if (res.data.success) setTeachers(res.data.data)
      } catch {
        setModalError('โหลดรายชื่อบุคลากรไม่สำเร็จ')
      }
    }
  }

  const visibleTeachers = useMemo(() => {
    const f = filter.trim().toLowerCase()
    return teachers.filter(t =>
      !f || t.t_name.toLowerCase().includes(f) || (t.hp_department || '').toLowerCase().includes(f))
  }, [teachers, filter])

  const toggle = (tid) => setSelected(prev => {
    const next = new Map(prev)
    if (next.has(tid)) next.delete(tid)
    else next.set(tid, '')
    return next
  })

  const handleAdd = async (e) => {
    e.preventDefault()
    if (!selected.size) {
      setModalError('กรุณาเลือกผู้เกี่ยวข้องอย่างน้อย 1 คน')
      return
    }
    setSaving(true)
    setModalError('')
    try {
      const fd = new FormData()
      fd.append('od_title', order.od_title)
      fd.append('od_date', order.od_date)
      fd.append('od_detail', order.od_detail || '')
      fd.append('assignees', JSON.stringify([...selected].map(([t_id, role]) => ({ t_id, role }))))
      await axiosInstance.put(`/api/ao/orders/${odId}`, fd)
      setShowAdd(false)
      fetchData()
    } catch (err) {
      setModalError(err.response?.data?.message || 'เพิ่มผู้เกี่ยวข้องไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const handleRemove = async (a) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: `นำ ${a.t_name} ออกจากรายชื่อ?`,
      showCancelButton: true,
      confirmButtonText: 'นำออก',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#dc3545',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.delete(`/api/ao/orders/${odId}/assignees/${a.oa_id}`)
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'นำออกไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const handleCancel = async () => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'ยกเลิกเอกสารฉบับนี้?',
      text: `${order.od_full_no} — ${order.od_title}`,
      showCancelButton: true,
      confirmButtonText: 'ยกเลิกเอกสาร',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#dc3545',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.put(`/api/ao/orders/${odId}/cancel`)
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ยกเลิกไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const tt = order ? (ORDER_TYPES[order.od_type] || ORDER_TYPES.order) : null

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <h4 className="main-title mb-0">
              <Link to="/admin-office/orders" className="text-decoration-none me-2" title="กลับทะเบียน">
                <i className="ri-arrow-left-line" />
              </Link>
              <i className="ri-megaphone-line me-2 text-primary" />
              {tt ? `${tt.label} ที่ ${order.od_full_no}` : 'รายละเอียดเอกสาร'}
            </h4>
            {order && order.od_status === 'active' && (
              <div className="d-flex gap-2">
                <Button variant="primary" size="sm" onClick={openAdd}>
                  <i className="ri-user-add-line me-1" />เพิ่มผู้เกี่ยวข้อง
                </Button>
                <Button variant="outline-danger" size="sm" onClick={handleCancel}>
                  <i className="ri-close-circle-line me-1" />ยกเลิกเอกสาร
                </Button>
              </div>
            )}
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : !order ? (
            <Alert variant="warning">ไม่พบเอกสาร</Alert>
          ) : (
            <Row className="g-3">
              <Col lg={5}>
                <Card className="card-one">
                  <Card.Header className="d-flex align-items-center justify-content-between">
                    <Card.Title as="h6" className="mb-0">รายละเอียด</Card.Title>
                    {order.od_status === 'cancelled'
                      ? <Badge bg="secondary">ยกเลิก</Badge>
                      : <Badge bg="success">ใช้งาน</Badge>}
                  </Card.Header>
                  <Card.Body className="fs-sm">
                    <Table borderless size="sm" className="mb-0">
                      <tbody>
                        <tr><td className="text-muted" style={{ width: 110 }}>ประเภท</td><td><Badge bg={tt.bg}>{tt.label}</Badge></td></tr>
                        <tr><td className="text-muted">เลขที่</td><td className="fw-semibold">{order.od_full_no}</td></tr>
                        <tr><td className="text-muted">วันที่</td><td>{fmtDateTH(order.od_date)}</td></tr>
                        <tr><td className="text-muted">ชื่อเรื่อง</td><td className="fw-semibold">{order.od_title}</td></tr>
                        {order.od_detail && <tr><td className="text-muted">รายละเอียด</td><td style={{ whiteSpace: 'pre-wrap' }}>{order.od_detail}</td></tr>}
                        <tr>
                          <td className="text-muted">ไฟล์</td>
                          <td>
                            {order.od_file ? (
                              <a href={`${UPLOAD_BASE}/uploads/ao_orders/${order.od_file}`} target="_blank" rel="noreferrer">
                                <i className="ri-attachment-2 me-1" />เปิดไฟล์ฉบับลงนาม
                              </a>
                            ) : '-'}
                          </td>
                        </tr>
                        <tr><td className="text-muted">ผู้จัดทำ</td><td>{order.created_by_name || '-'} · {fmtDateTimeTH(order.created_at)}</td></tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={7}>
                <Card className="card-one">
                  <Card.Header>
                    <Card.Title as="h6" className="mb-0">
                      ผู้เกี่ยวข้อง
                      {order.assignees.length > 0 && (
                        <span className="text-muted fw-normal ms-2 fs-sm">
                          รับทราบแล้ว {order.assignees.filter(a => a.oa_ack).length}/{order.assignees.length} คน
                        </span>
                      )}
                    </Card.Title>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {order.assignees.length === 0 ? (
                      <div className="text-center text-muted py-4">ยังไม่ได้ระบุผู้เกี่ยวข้อง</div>
                    ) : (
                      <Table responsive hover className="mb-0 fs-sm">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 40 }}>#</th>
                            <th>ชื่อ</th>
                            <th>บทบาท</th>
                            <th style={{ width: 160 }}>รับทราบ</th>
                            <th style={{ width: 50 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.assignees.map((a, i) => (
                            <tr key={a.oa_id}>
                              <td>{i + 1}</td>
                              <td>{a.t_name}</td>
                              <td>{a.oa_role || '-'}</td>
                              <td>
                                {a.oa_ack ? (
                                  <>
                                    <Badge bg="success"><i className="ri-check-line" /> รับทราบ</Badge>
                                    <div className="text-muted" style={{ fontSize: '0.72rem' }}>{fmtDateTimeTH(a.oa_ack_at)}</div>
                                  </>
                                ) : (
                                  <Badge bg="warning">รอรับทราบ</Badge>
                                )}
                              </td>
                              <td className="text-end">
                                {order.od_status === 'active' && !a.oa_ack && (
                                  <Button size="sm" variant="outline-danger" title="นำออก" onClick={() => handleRemove(a)}>
                                    <i className="ri-delete-bin-line" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
      <Footer />

      {/* Add-assignees modal */}
      <Modal show={showAdd} onHide={() => setShowAdd(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">เพิ่มผู้เกี่ยวข้อง</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAdd}>
          <Modal.Body>
            {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}
            <Form.Control size="sm" className="mb-2" placeholder="ค้นหาชื่อ/ฝ่าย"
              value={filter} onChange={e => setFilter(e.target.value)} />
            <div className="border rounded" style={{ maxHeight: 300, overflowY: 'auto' }}>
              {visibleTeachers.map(t => {
                const assigned = alreadyAssigned.has(t.t_id)
                return (
                  <div key={t.t_id} className="d-flex align-items-center px-3 py-1 border-bottom gap-2">
                    <Form.Check type="checkbox" id={`add-t-${t.t_id}`} className="flex-grow-1"
                      label={<span className={assigned ? 'text-muted' : ''}>{t.t_name}{t.hp_department ? ` · ${t.hp_department}` : ''}{assigned ? ' (อยู่ในรายชื่อแล้ว)' : ''}</span>}
                      checked={selected.has(t.t_id)}
                      disabled={assigned}
                      onChange={() => toggle(t.t_id)} />
                    {selected.has(t.t_id) && order?.od_type === 'appointment' && (
                      <Form.Control size="sm" style={{ width: 160 }} placeholder="บทบาท"
                        value={selected.get(t.t_id)}
                        onChange={e => setSelected(prev => new Map(prev).set(t.t_id, e.target.value))} />
                    )}
                  </div>
                )
              })}
              {!visibleTeachers.length && <div className="text-center text-muted py-3 fs-sm">ไม่พบบุคลากร</div>}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowAdd(false)}>ยกเลิก</Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-user-add-line me-1" />}
              เพิ่ม ({selected.size} คน)
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}
